import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import SpreadsheetEditor, { toEditorRows, fromEditorRows } from './components/SpreadsheetEditor';
import ImportModal from './components/ImportModal';
import StockHeatmap, { METRIC_OPTIONS } from './components/StockHeatmap';
import {
  isSupabaseReady,
  listTransactionSheets,
  createTransactionSheet,
  getTransactionSheet,
  renameTransactionSheet,
  deleteTransactionSheet,
  getTransactionRows,
  saveTransactionRows,
} from './lib/supabase';
import { SECTOR_SHORT } from './lib/sp500';

async function fetchJsonWithFallback(urls) {
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status}`);
        continue;
      }
      return await res.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Request failed');
}

function SheetPicker({ onSelect, onNew }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listTransactionSheets()
      .then(setSheets)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="stonks-sheet-list-wrap">
      <div className="stonks-sheet-list-title">Transaction Portfolios</div>
      {!isSupabaseReady() && (
        <div className="stonks-db-notice">
          ⚠️ Supabase not configured for this environment — set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in Netlify production env vars, then redeploy.
        </div>
      )}
      <button className="btn-stonks btn-stonks-primary" onClick={onNew} style={{ width: 'fit-content' }}>
        ＋ New Portfolio
      </button>
      {loading && <div className="stonks-loading" style={{ flex: 'none', marginTop: 20 }}><div className="stonks-spinner" /></div>}
      {!loading && sheets.length === 0 && (
        <div className="stonks-empty" style={{ flex: 'none', marginTop: 20 }}>
          <div>No saved portfolios yet — create one to get started.</div>
        </div>
      )}
      {sheets.map(sheet => (
        <div key={sheet.id} className="stonks-sheet-list-item" onClick={() => onSelect(sheet.id)}>
          <div className="stonks-sheet-list-name">📋 {sheet.name}</div>
          <div className="stonks-sheet-list-date">
            {new Date(sheet.updated_at).toLocaleDateString()}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Transactions() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view       = searchParams.get('view')     || 'edit';
  const metric     = searchParams.get('metric')   || '1d';
  const filterType = searchParams.get('filter')   || 'all'; // 'all' | 'purchases' | 'sales'
  const sectors    = searchParams.get('sectors')  || 'all';
  const subindustry = searchParams.get('subindustry') || 'all';
  const dateStart  = searchParams.get('start')    || '';
  const dateEnd    = searchParams.get('end')      || '';
  const [taxonomyRows, setTaxonomyRows] = useState([]);

  // Multi-sector chip state
  const selectedSectors = sectors === 'all'
    ? new Set()
    : new Set(sectors.split(',').map(s => s.trim()).filter(Boolean));

  const availableSectors = useMemo(() => {
    const set = new Set(taxonomyRows.map(r => r.sector).filter(Boolean));
    set.add('Other');
    return [...set].sort();
  }, [taxonomyRows]);

  const availableSubIndustries = useMemo(() => {
    const subs = taxonomyRows
      .filter(r => !selectedSectors.size || selectedSectors.has(r.sector))
      .map(r => r.sub)
      .filter(Boolean);
    const set = new Set(subs);
    set.add('NA');
    return [...set].sort();
  }, [taxonomyRows, sectors]);

  function toggleSector(s) {
    const next = new Set(selectedSectors);
    if (next.has(s)) next.delete(s); else next.add(s);
    setParam('sectors', next.size ? [...next].join(',') : 'all');
  }

  const [sheetName, setSheetName]   = useState('New Portfolio');
  const [rows, setRows]             = useState([{ id: 1, ticker: '', type: '', date: '', amount: '' }]);
  const [saving, setSaving]         = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [loadingSheet, setLoadingSheet] = useState(false);

  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Load sheet from DB when sheetId changes
  useEffect(() => {
    if (!sheetId) return;
    setLoadingSheet(true);
    Promise.all([
      getTransactionSheet(sheetId),
      getTransactionRows(sheetId),
    ]).then(([sheet, dbRows]) => {
      if (sheet?.name) setSheetName(sheet.name);
      setRows(toEditorRows(dbRows));
    }).finally(() => setLoadingSheet(false));
  }, [sheetId]);

  const handleNew = useCallback(async () => {
    const sheet = await createTransactionSheet('New Portfolio');
    if (sheet) {
      navigate(`/stonks/transactions/${sheet.id}`);
    } else {
      // Supabase not configured: just open a local sheet
      navigate(`/stonks/transactions/local`);
    }
  }, [navigate]);

  const handleSelect = useCallback((id) => {
    navigate(`/stonks/transactions/${id}`);
  }, [navigate]);

  const handleRename = useCallback(async (name) => {
    setSheetName(name);
    if (sheetId && sheetId !== 'local') {
      await renameTransactionSheet(sheetId, name);
    }
  }, [sheetId]);

  const handleDelete = useCallback(async () => {
    if (!sheetId || sheetId === 'local') { navigate('/stonks/transactions'); return; }
    if (!window.confirm('Delete this portfolio? This cannot be undone.')) return;
    await deleteTransactionSheet(sheetId);
    navigate('/stonks/transactions');
  }, [sheetId, navigate]);

  const handleVisualize = useCallback(async () => {
    const validRows = fromEditorRows(rows);
    if (validRows.length === 0) {
      alert('Add at least one ticker before visualizing.');
      return;
    }
    setSaving(true);
    if (sheetId && sheetId !== 'local') {
      await saveTransactionRows(sheetId, validRows);
    }
    setSaving(false);
    setParam('view', 'visualize');
  }, [rows, sheetId, setParam]);

  const handleImport = useCallback((importedRows) => {
    setRows(toEditorRows(importedRows));
  }, []);

  // ── Filter rows for heatmap ───────────────────────────────────────────────
  const validRows = fromEditorRows(rows);
  const filteredRows = validRows.filter(r => {
    if (filterType === 'purchases' && r.type !== 'purchase' && r.type !== '') return false;
    if (filterType === 'sales'     && r.type !== 'sale') return false;
    if (dateStart && r.date && r.date < dateStart) return false;
    if (dateEnd   && r.date && r.date > dateEnd)   return false;
    return true;
  });

  const uniqueTickers = [...new Set(filteredRows.map(r => r.ticker).filter(Boolean))];

  // Pull taxonomy from Yahoo Finance for currently-visible transaction tickers.
  useEffect(() => {
    if (!uniqueTickers.length) {
      setTaxonomyRows([]);
      return;
    }
    const tickerParam = encodeURIComponent(uniqueTickers.join(','));
    fetchJsonWithFallback([
      `/api/stonksMarket?tickers=${tickerParam}&includeProfile=1`,
      `/.netlify/functions/stonksMarket?tickers=${tickerParam}&includeProfile=1`,
    ])
      .then(data => {
        const quotes = data?.quotes || {};
        const rows = uniqueTickers.map(t => {
          const q = quotes[t] || {};
          return {
            sector: q.sector || 'Other',
            sub: q.industry || 'NA',
          };
        });
        setTaxonomyRows(rows);
      })
      .catch(() => setTaxonomyRows([]));
  }, [uniqueTickers.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── No sheetId: show picker ───────────────────────────────────────────────
  if (!sheetId) {
    return <SheetPicker onSelect={handleSelect} onNew={handleNew} />;
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loadingSheet) {
    return <div className="stonks-loading"><div className="stonks-spinner" /><span>Loading…</span></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Ribbon ───────────────────────────────────────────────── */}
      <div className="stonks-sheet-ribbon">
        <input
          className="stonks-sheet-name-input"
          value={sheetName}
          onChange={e => setSheetName(e.target.value)}
          onBlur={e => handleRename(e.target.value)}
        />

        {view === 'edit' && (
          <>
            <button className="btn-stonks btn-stonks-secondary" onClick={() => setShowImport(true)}>
              📥 Import
            </button>
            <button
              className="btn-stonks btn-stonks-green"
              onClick={handleVisualize}
              disabled={saving}
            >
              {saving ? '⏳ Saving…' : '🗺 Visualize'}
            </button>
          </>
        )}

        {view === 'visualize' && (
          <>
            <button className="btn-stonks btn-stonks-secondary" onClick={() => setParam('view', 'edit')}>
              ✏️ Edit Data
            </button>
          </>
        )}

        <div className="stonks-sheet-ribbon-right">
          <button className="btn-stonks btn-stonks-ghost" onClick={() => navigate('/stonks/transactions')} title="Back to list">
            ← All Portfolios
          </button>
          <button className="btn-stonks btn-stonks-danger" onClick={handleDelete}>
            🗑 Delete
          </button>
        </div>
      </div>

      {/* ── Filter bar (visualize mode) ───────────────────────────── */}
      {view === 'visualize' && (
        <div className="stonks-filterbar">
          <span className="stonks-filterbar-label">Color</span>
          <div className="stonks-chip-group">
            {METRIC_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`stonks-chip${metric === opt.value ? ' active' : ''}`}
                onClick={() => setParam('metric', opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="stonks-topbar-divider" />

          <span className="stonks-filterbar-label">Show</span>
          <div className="stonks-chip-group">
            {['all', 'purchases', 'sales'].map(f => (
              <button
                key={f}
                className={`stonks-chip${filterType === f ? ' active' : ''}`}
                onClick={() => setParam('filter', f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="stonks-topbar-divider" />

          <span className="stonks-filterbar-label">Sector</span>
          <div className="stonks-chip-group" style={{ flexWrap: 'wrap', maxWidth: 540 }}>
            <button
              className={`stonks-chip${!selectedSectors.size ? ' active' : ''}`}
              onClick={() => setParam('sectors', 'all')}
            >All</button>
            {availableSectors.map(s => (
              <button
                key={s}
                className={`stonks-chip${selectedSectors.has(s) ? ' active' : ''}`}
                onClick={() => toggleSector(s)}
                title={s}
              >
                {SECTOR_SHORT[s] || s}
              </button>
            ))}
          </div>

          <div className="stonks-topbar-divider" />

          <span className="stonks-filterbar-label">Sub-Industry</span>
          <select
            className="stonks-select"
            value={subindustry}
            onChange={e => setParam('subindustry', e.target.value)}
          >
            <option value="all">All Sub-Industries</option>
            {availableSubIndustries.map(sub => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </select>

          <div className="stonks-topbar-divider" />

          <span className="stonks-filterbar-label">Dates</span>
          <input
            className="stonks-input"
            type="date"
            value={dateStart}
            onChange={e => setParam('start', e.target.value)}
            style={{ fontFamily: 'var(--s-mono)', fontSize: 11, colorScheme: 'dark' }}
          />
          <span style={{ color: 'var(--s-text-muted)', fontSize: 11 }}>→</span>
          <input
            className="stonks-input"
            type="date"
            value={dateEnd}
            onChange={e => setParam('end', e.target.value)}
            style={{ fontFamily: 'var(--s-mono)', fontSize: 11, colorScheme: 'dark' }}
          />
          {(dateStart || dateEnd) && (
            <button
              className="btn-stonks btn-stonks-ghost"
              style={{ fontSize: 11, padding: '2px 8px' }}
              onClick={() => {
                setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  next.delete('start'); next.delete('end');
                  return next;
                }, { replace: true });
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {/* ── Main content area ─────────────────────────────────────── */}
      {view === 'edit' ? (
        <SpreadsheetEditor rows={rows} onChange={setRows} />
      ) : (
        <>
          <StockHeatmap
            mode="transactions"
            tickers={uniqueTickers}
            transactions={filteredRows}
            metric={metric}
            sectorFilter={sectors}
            subindustryFilter={subindustry}
          />
          <div className="stonks-legend">
            <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--s-text-muted)' }}>
              <span><span style={{ background: 'hsl(145,60%,40%)', padding: '2px 8px', borderRadius: 3, marginRight: 4, fontSize: 10 }} />Buy (darker = larger)</span>
              <span><span style={{ background: 'hsl(4,70%,35%)', padding: '2px 8px', borderRadius: 3, marginRight: 4, fontSize: 10 }} />Sell (darker = larger)</span>
              <span style={{ marginLeft: 'auto' }}>Size = Market Cap · Stripes = multiple transactions (newest top)</span>
            </div>
          </div>
        </>
      )}

      {/* ── Import modal ──────────────────────────────────────────── */}
      {showImport && (
        <ImportModal
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}
